'use strict';

var FeedStore = require('./FeedStore'),
    UserStore = require('./UserStore'),
    TrendingStore = require('./TrendingStore'),
    StampStore = require('./StampStore'),
    ScreenSizeStore = require('./ScreenSizeStore'),
    getStampCountPerRow = require('../utils/getStampCountPerRow'),
    { createStore } = require('../utils/StoreUtils'),
    { chain, filter, groupBy, isArray, last, pluck, uniq, values } = require('underscore');

var FEED_PREFERRED_COLUMNS = 3,
    FEED_CONSECUTIVE_ROWS = 2,
    TRENDING_CONSECUTIVE_ROWS = 1;

/**
 * Makes sure that duplicate trending stamp is not in feed or at least far enough from feed stamp
 * (so we don't have to refilter whole trending after fetching more feed)
 */
function filterDuplicatesFromTrending(trendingStampIds, feedStampIds) {
  var screenSize = ScreenSizeStore.getScreenSize(),
      stampsPerRow = getStampCountPerRow(screenSize, FEED_PREFERRED_COLUMNS);

  return filter(trendingStampIds, (stampId, idx) => {
    var feedIdx = feedStampIds.indexOf(stampId),
        trendingRow = Math.floor(idx / stampsPerRow),
        feedRow = Math.floor(feedIdx / stampsPerRow);

    return feedIdx === -1 || feedRow > (trendingRow * (FEED_CONSECUTIVE_ROWS / TRENDING_CONSECUTIVE_ROWS) + FEED_CONSECUTIVE_ROWS);
  });
}

function groupByRow(stampIds, consecutiveRows) {
  var screenSize = ScreenSizeStore.getScreenSize(),
      stampsPerRow = getStampCountPerRow(screenSize, FEED_PREFERRED_COLUMNS);

  return groupBy(stampIds, (val, key) =>
    Math.floor(key / (consecutiveRows * stampsPerRow)
  ));
}

function getGroupedIds() {
  if (!UserStore.isAuthenticated()) {
    return {
      groupedFeedIds: [],
      groupedTrendingIds: [TrendingStore.getIds()]
    };
  }

  if (!TrendingStore.getPageCount() || !FeedStore.getPageCount()) {
    return {
      groupedFeedIds: [],
      groupedTrendingIds: []
    };
  }

  if (!FeedStore.getIds().length) {
    return {
      groupedFeedIds: [],
      groupedTrendingIds: [TrendingStore.getIds()]
    };
  }

  var feedStampIds = FeedStore.getIds(),
      trendingStampIds = filterDuplicatesFromTrending(TrendingStore.getIds(), feedStampIds),
      screenSize = ScreenSizeStore.getScreenSize(),
      stampsPerRow = getStampCountPerRow(screenSize, FEED_PREFERRED_COLUMNS),
      groupedFeedIds = values(groupByRow(feedStampIds, FEED_CONSECUTIVE_ROWS)),
      groupedTrendingIds = values(groupByRow(trendingStampIds, TRENDING_CONSECUTIVE_ROWS));

  // Throw away last few trending stamps that don't make a nice row
  if (groupedTrendingIds.length && last(groupedTrendingIds).length < (TRENDING_CONSECUTIVE_ROWS * stampsPerRow)) {
    groupedTrendingIds.splice(groupedTrendingIds.length - 1, 1);
  }

  return {
    groupedFeedIds: groupedFeedIds,
    groupedTrendingIds: groupedTrendingIds
  };
}

var FeedWithTrendingStore = createStore({
  getStamps() {
    return uniq(pluck(this.getKeyedStamps(), 'item'));
  },

  getKeyedStamps() {
    var { groupedFeedIds, groupedTrendingIds } = getGroupedIds(),
        groupedFeedStamps,
        groupedTrendingStamps;

    groupedFeedStamps = groupedFeedIds.map(group => group.map(id => {
      return {
        key: id + '-feed',
        item: StampStore.get(id),
        isTrending: false
      };
    }));

    groupedTrendingStamps = groupedTrendingIds.map(group => group.map(id => {
      return {
        key: id + '-trending',
        item: StampStore.get(id),
        isTrending: true
      };
    }));

    return chain(groupedFeedStamps)
      .zip(groupedTrendingStamps)
      // Avoid lots of trending getting ahead of next Feed page, unless that's the last one:
      .filter(zipped => isArray(zipped[0]) || FeedStore.isLastPage())
      .flatten()
      .compact()
      .value();
  },

  isExpectingPage() {
    return FeedStore.isExpectingPage() || TrendingStore.isExpectingPage();
  },

  isLastPage() {
    return FeedStore.isLastPage() && TrendingStore.isLastPage();
  },

  isInvalidated() {
    return FeedStore.isInvalidated() || TrendingStore.isInvalidated();
  },

  needFeed(isInitialRequest) {
    if (!UserStore.isAuthenticated()) {
      return false;
    }

    var isEmpty = !FeedStore.getIds().length;
    return (isInitialRequest && isEmpty) || !FeedStore.isLastPage() || FeedStore.isInvalidated();
  },

  needTrending(isInitialRequest) {
    var isEmpty = !TrendingStore.getIds().length;
    return (isInitialRequest && isEmpty) || !TrendingStore.isLastPage() || TrendingStore.isInvalidated();
  }
});

FeedStore.addChangeListener(() =>
  FeedWithTrendingStore.emitChange()
);

TrendingStore.addChangeListener(() =>
  FeedWithTrendingStore.emitChange()
);

ScreenSizeStore.addChangeListener(() =>
  FeedWithTrendingStore.emitChange()
);

module.exports = FeedWithTrendingStore;


/** WEBPACK FOOTER **
 ** ./stores/FeedWithTrendingStore.js
 **/